import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
    CreateGoalDto,
    GoalDeletedEvent,
    GoalDto,
    GoalSpaceDto,
    UpdateGoalDto,
    UpdateGoalSpaceDto,
} from '../../../../shared/types/goalSpace';
import { GOAL_SPACE_EVENTS } from '../../../../shared/types/goalSpace';
import { api, shouldRetry } from '../../services/api';
import type { LiveHandlers } from '../../services/live';
import { patchQuery } from '../../services/restartReload';
import { platformKeys } from '../../services/platform';
import { uploadImage } from '../../services/images';
import type { NewSpaceAccessDto } from '../../../../shared/types/access';

export const goalKeys = {
    space: (shareId: string) => ['goalSpace', shareId] as const,
    goals: (shareId: string) => ['goalSpace', shareId, 'goals'] as const,
};

const base = (shareId: string) => `/api/goal-spaces/${encodeURIComponent(shareId)}`;

export async function createSpace(title: string, access: NewSpaceAccessDto): Promise<GoalSpaceDto> {
    return (await api.post<GoalSpaceDto>('/api/goal-spaces', { title, ...access })).data;
}

/** Upload a cropped banner for a space that exists, and show it. */
export async function setSpaceBanner(shareId: string, file: File): Promise<void> {
    const bannerImage = await uploadImage('goalSpace', shareId, file);
    await api.patch(base(shareId), { bannerImage });
}

/** Bring an archived space back (from Home's Archived list). */
export async function restoreSpace(shareId: string): Promise<void> {
    await api.patch(base(shareId), { archived: false });
}

export function useGoalSpace(shareId: string | undefined, enabled = true) {
    return useQuery({
        queryKey: goalKeys.space(shareId ?? ''),
        queryFn: async () => (await api.get<GoalSpaceDto>(base(shareId!))).data,
        enabled: !!shareId && enabled,
        retry: shouldRetry,
    });
}

export function useGoals(shareId: string | undefined, enabled = true) {
    return useQuery({
        queryKey: goalKeys.goals(shareId ?? ''),
        queryFn: async () => (await api.get<GoalDto[]>(`${base(shareId!)}/goals`)).data,
        enabled: !!shareId && enabled,
        retry: shouldRetry,
    });
}

// ─── Cache updates (shared by mutations and live events) ───────────────────

export function upsertGoal(qc: QueryClient, shareId: string, goal: GoalDto) {
    patchQuery<GoalDto[]>(qc, goalKeys.goals(shareId), list => {
        const i = list.findIndex(g => g._id === goal._id);
        if (i === -1) return [...list, goal];
        // Ignore stale echoes (our own mutation result may arrive after the live event)
        if (list[i].updatedAt > goal.updatedAt) return list;
        const next = list.slice();
        next[i] = goal;
        return next;
    });
}

/** Remove a goal; goals aligned under it move up to its parent, as on the server. */
export function removeGoal(qc: QueryClient, shareId: string, ev: GoalDeletedEvent) {
    patchQuery<GoalDto[]>(qc, goalKeys.goals(shareId), list => list
        .filter(g => g._id !== ev._id)
        .map(g => (g.alignedToGoalId === ev._id ? { ...g, alignedToGoalId: ev.alignedToGoalId } : g)));
}

/** Live event handlers for useResourcePage. */
export function goalLiveHandlers(qc: QueryClient, shareId: string | undefined): LiveHandlers {
    if (!shareId) return {};
    return {
        [GOAL_SPACE_EVENTS.goalCreated]: (g: GoalDto) => upsertGoal(qc, shareId, g),
        [GOAL_SPACE_EVENTS.goalUpdated]: (g: GoalDto) => upsertGoal(qc, shareId, g),
        [GOAL_SPACE_EVENTS.goalDeleted]: (ev: GoalDeletedEvent) => removeGoal(qc, shareId, ev),
        [GOAL_SPACE_EVENTS.spaceUpdated]: (s: GoalSpaceDto) => qc.setQueryData(goalKeys.space(shareId), s),
    };
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export function useCreateGoal(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (dto: CreateGoalDto) => (await api.post<GoalDto>(`${base(shareId)}/goals`, dto)).data,
        onSuccess: g => upsertGoal(qc, shareId, g),
    });
}

export function useUpdateGoal(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, dto }: { id: string; dto: UpdateGoalDto }) =>
            (await api.put<GoalDto>(`${base(shareId)}/goals/${id}`, dto)).data,
        onSuccess: g => upsertGoal(qc, shareId, g),
    });
}

export function useDeleteGoal(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (goal: GoalDto) => {
            await api.delete(`${base(shareId)}/goals/${goal._id}`);
            return goal;
        },
        onSuccess: g => removeGoal(qc, shareId, { _id: g._id, alignedToGoalId: g.alignedToGoalId }),
    });
}

export function useUpdateSpace(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (dto: UpdateGoalSpaceDto) => (await api.patch<GoalSpaceDto>(base(shareId), dto)).data,
        onSuccess: s => {
            qc.setQueryData(goalKeys.space(shareId), s);
            qc.invalidateQueries({ queryKey: platformKeys.all });
        },
    });
}

export function useDeleteSpace(shareId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => api.delete(base(shareId)),
        onSuccess: () => qc.invalidateQueries({ queryKey: platformKeys.all }),
    });
}
